import {
  DashboardCreatePayload,
  DashboardDto,
  DashboardMutatePayload,
  DataSourceSettingsDto,
  MutateWidgetPayload,
} from '@thingslog/repositories';
import { QueryKeys } from '@thingslog/queries';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { dashboardQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';

interface UseDashboardOperationsResult {
  handlePersistNewDashboard: (
    payload: DashboardMutatePayload,
    callbacks?: DashboardMutationCallbacks,
    options?: DashboardCreateOptions
  ) => void;
  handlePersistDashboard: (
    dashboardId: string,
    payload: DashboardMutatePayload,
    callbacks?: DashboardMutationCallbacks
  ) => void;
  handleDeleteDashboard: (dashboardId: string, callbacks?: DashboardMutationCallbacks) => void;
  isPersistingNewDashboard: boolean;
  isPersistingDashboard: boolean;
  isDeletingDashboard: boolean;
}

interface DashboardMutationCallbacks {
  onSuccess?: () => void;
}

interface DashboardCreateOptions {
  includeDefaultWidgets?: boolean;
  isDefault?: boolean;
  isFavorite?: boolean;
}

const DEFAULT_NEW_DASHBOARD_WIDGETS: MutateWidgetPayload[] = [
  {
    settings: {
      type: 'pie-chart',
      title: 'Alarms',
      query: {
        type: 'api-alarms-count',
        timespan: null,
      },
      dataSourceId: '',
      visualization: {
        showLegend: false,
        shape: 'pie',
      },
    },
  },

  {
    settings: {
      type: 'bar-chart',
      title: 'Battery',
      query: {
        type: 'api-battery-histogram',
      },
      dataSourceId: '',
      visualization: {
        showGrid: true,
        showLegend: false,
        showXAxisLabels: true,
        showYAxisLabels: true,
        orientation: 'vertical',
        grouping: 'grouped',
        barThickness: 'regular',
      },
    },
  },
  {
    settings: {
      type: 'table',
      title: 'Alarms Table',
      query: {
        type: 'api-alarms-count',
        timespan: null,
      },
      dataSourceId: '',
      visualization: {
        paginated: false,
      },
    },
  },
  {
    settings: {
      type: 'bar-chart',
      title: 'Signal',
      query: {
        type: 'api-signal-histogram',
      },
      dataSourceId: '',
      visualization: {
        showGrid: true,
        showLegend: false,
        showXAxisLabels: true,
        showYAxisLabels: true,
        orientation: 'vertical',
        grouping: 'grouped',
        barThickness: 'regular',
      },
    },
  },
];

const resolveWidgetDataSources = (
  widgets: MutateWidgetPayload[],
  dataSources: DataSourceSettingsDto[]
): MutateWidgetPayload[] => {
  return widgets.reduce<MutateWidgetPayload[]>(
    (
      resolvedWidgets: MutateWidgetPayload[],
      widget: MutateWidgetPayload
    ): MutateWidgetPayload[] => {
      const dataSource = dataSources.find(
        (dataSourceItem: DataSourceSettingsDto): boolean =>
          dataSourceItem.source.type === widget.settings.query.type
      );

      if (!dataSource) {
        return resolvedWidgets;
      }

      return [
        ...resolvedWidgets,
        {
          ...widget,
          settings: {
            ...widget.settings,
            dataSourceId: dataSource.id,
          },
        },
      ];
    },
    []
  );
};

export const useDashboardOperations = (
  companyId?: string | number | null,
  dataSources: DataSourceSettingsDto[] = [],
  canCreateDefaultDashboard: boolean = false
): UseDashboardOperationsResult => {
  const { useCreateDashboard, useUpdateDashboard, useDeleteDashboard } = dashboardQueryClient;
  const queryClient = useQueryClient();

  const createDashboardMutation = useCreateDashboard({
    onSuccess: (dashboard: DashboardDto): void => {
      queryClient.invalidateQueries([QueryKeys.GetDashboards]);
      queryClient.invalidateQueries([QueryKeys.GetDashboard, dashboard.id, companyId]);
    },
  });

  const updateDashboardMutation = useUpdateDashboard({
    onSuccess: (
      dashboard: DashboardDto,
      variables: { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
    ): void => {
      queryClient.invalidateQueries([QueryKeys.GetDashboards]);
      queryClient.invalidateQueries([QueryKeys.GetDashboard, variables.id, variables.companyId]);
      queryClient.invalidateQueries([QueryKeys.GetDashboard, dashboard.id, variables.companyId]);
    },
  });

  const deleteDashboardMutation = useDeleteDashboard({
    onSuccess: (
      _result: boolean,
      variables: string | { id: string; companyId?: string | number | null }
    ): void => {
      const dashboardId = typeof variables === 'string' ? variables : variables.id;
      const dashboardCompanyId = typeof variables === 'string' ? companyId : variables.companyId;

      queryClient.invalidateQueries([QueryKeys.GetDashboards]);
      queryClient.invalidateQueries([QueryKeys.GetDashboard, dashboardId, dashboardCompanyId]);
    },
  });

  const handlePersistNewDashboard = useCallback(
    (
      payload: DashboardMutatePayload,
      callbacks?: DashboardMutationCallbacks,
      options: DashboardCreateOptions = {}
    ): void => {
      const { includeDefaultWidgets = false, isDefault = false, isFavorite = false } = options;
      const shouldCreateDefaultDashboard = includeDefaultWidgets && canCreateDefaultDashboard;
      const createPayload: DashboardCreatePayload = {
        ...payload,
        widgets: shouldCreateDefaultDashboard
          ? resolveWidgetDataSources(DEFAULT_NEW_DASHBOARD_WIDGETS, dataSources)
          : [],
        isDefault: shouldCreateDefaultDashboard ? isDefault : false,
        isFavorite: shouldCreateDefaultDashboard ? isFavorite : false,
      };

      createDashboardMutation.mutate(
        {
          ...createPayload,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [canCreateDefaultDashboard, companyId, createDashboardMutation, dataSources]
  );

  const handlePersistDashboard = useCallback(
    (
      dashboardId: string,
      payload: DashboardMutatePayload,
      callbacks?: DashboardMutationCallbacks
    ): void => {
      if (!dashboardId) {
        return;
      }

      updateDashboardMutation.mutate(
        {
          id: dashboardId,
          body: payload,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [companyId, updateDashboardMutation]
  );

  const handleDeleteDashboard = useCallback(
    (dashboardId: string, callbacks?: DashboardMutationCallbacks): void => {
      if (!dashboardId) {
        return;
      }

      deleteDashboardMutation.mutate(
        {
          id: dashboardId,
          companyId,
        },
        { onSuccess: callbacks?.onSuccess }
      );
    },
    [companyId, deleteDashboardMutation]
  );

  return {
    handlePersistNewDashboard,
    handlePersistDashboard,
    handleDeleteDashboard,
    isPersistingNewDashboard: createDashboardMutation.isLoading,
    isPersistingDashboard: updateDashboardMutation.isLoading,
    isDeletingDashboard: deleteDashboardMutation.isLoading,
  };
};
